import React from "react";
import { motion } from "framer-motion";

const pricingTiers = [
  {
    title: "Starter",
    description:
      "Full-featured free trial — no card required. Register with your business email to get 5 free URO credits.",
    price: "Free",
    features: [
      "5 URO credits on signup",
      "Try any model instantly, no credit card",
      "Full API & dashboard access",
      "Join 1,000+ developers building with Uro",
    ],
    highlight: true,
  },
  {
    title: "Pay As You Go",
    description:
      "Purchase URO credits on demand. $1 = 1 URO credit (cost varies by model & GPU time).",
    price: "$1 = 1 URO Credit",
    features: [
      "Generate up to 175 images with Flux 1 Dev for 1 URO (~$0.005 per image)",
      "Flexible pay-as-you-go — no monthly fees",
      "Realtime cost dashboard",
      "Only pay when you use",
    ],
    highlight: false,
  },
  {
    title: "Enterprise",
    description: "For teams and high volume users. Custom pricing and support.",
    price: "Contact Us",
    features: [
      "Custom integrations",
      "Priority support",
      "Dedicated account manager",
      "Volume discounts",
    ],
    highlight: false,
  },
];

const PricingScreen = () => {
  return (
    <section
      id="pricing"
      className="min-h-[80vh] py-24 px-4  flex flex-col items-center justify-center"
    >
      <motion.h1
        className="text-4xl md:text-5xl font-bold text-center mb-6 text-white tracking-tight"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        Pricing & Credits
      </motion.h1>
      <p className="text-lg text-gray-300 text-center max-w-2xl mb-12">
        All users start with{" "}
        <span className="font-bold text-[#b74bff]">5 URO credits</span> to test
        the platform. Pricing is based on GPU time and model usage. Purchase
        more credits as you grow!
      </p>
      <div className="w-full max-w-5xl grid grid-cols-1 md:grid-cols-3 gap-8">
        {pricingTiers.map((tier, idx) => (
          <motion.div
            key={tier.title}
            className={`rounded-3xl shadow-2xl border border-white/10 bg-[#232646]/90 p-8 flex flex-col items-center text-center transition-all duration-200 ${
              tier.highlight ? "ring-2 ring-[#b74bff] scale-105" : ""
            }`}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{
              duration: 0.7,
              delay: idx * 0.15,
              type: "spring",
              bounce: 0.18,
            }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
              {tier.title}
            </h3>
            <p className="text-base text-gray-300 mb-4">{tier.description}</p>
            <div className="text-3xl font-extrabold text-[#b74bff] mb-6">
              {tier.price}
            </div>
            <ul className="text-gray-200 text-left mb-8 space-y-2">
              {tier.features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#b74bff] inline-block" />{" "}
                  {f}
                </li>
              ))}
            </ul>
            {tier.title === "Enterprise" ? (
              <a
                href="mailto:support@uro.ai"
                className="btn btn-pill text-white border border-white/30 px-7 py-3 rounded-full font-bold text-base shadow-none hover:bg-white/10 focus:ring-2 focus:ring-[#b74bff] focus:outline-none transition-all duration-150"
              >
                Coming Soon
                {/* Request Enterprise Access → */}
              </a>
            ) : tier.title === "Pay As You Go" ? (
              <div>
                <button
                  onClick={() =>
                    (window.location.href = "https://account.fluxuro.ai")
                  }
                  className="btn btn-pill border border-[#5a4bff] text-[#5a4bff] px-7 py-3 rounded-full font-bold text-base shadow-none hover:bg-[#5a4bff]/10 focus:ring-2 focus:ring-[#5a4bff] transition-all duration-150"
                >
                  Buy Credits
                </button>
                <a
                  href="/model-costs"
                  className="mt-3 inline-block text-sm text-gray-300 hover:text-white underline"
                >
                  View Model Cost Table →
                </a>
              </div>
            ) : (
              <button
                onClick={() =>
                  (window.location.href = "https://account.fluxuro.ai")
                }
                className="btn btn-pill bg-gradient-to-r from-[#5a4bff] to-[#b74bff] text-white px-7 py-3 rounded-full font-bold text-base shadow-xl hover:scale-105 focus:ring-2 focus:ring-[#b74bff] focus:outline-none transition-all duration-150"
              >
                {tier.highlight ? "Start Free" : "Buy Credits"}
              </button>
            )}
          </motion.div>
        ))}
      </div>
      <div className="mt-12 text-gray-400 text-sm max-w-2xl text-center">
        <p>
          Actual cost per generation depends on the model, prompt, and input
          parameters. The platform calculates GPU time per request and deducts
          the corresponding URO credits.{" "}
        </p>
      </div>
    </section>
  );
};

export default PricingScreen;
